import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {
  addOrReplaceAndRunAnalysisStep,
  copyAnalysisViewSteps,
  deleteAnalysisSteps,
  DeletePipelineStepsBehavior,
  updateAndRunAnalysisStep,
} from 'analysis-tool/AnalysisActions';
import {
  SINGLE_DATASET_INPUT_STEP_DATA_NAME,
  SINGLE_DATASET_OUTPUT_STEP_DATA_NAME,
} from 'analysis-tool/AnalysisStepDataNames';
import {SHARED_MAIN_VIEW_COMPONENT_WITH_MARGIN_STYLES} from 'analysis-tool/AnalysisToolConstants';
import {
  findAndAssertSingleStepDataFrameId,
  findAndAssertStepData,
} from 'analysis-tool/data/findAndAssertStepDataFrameIds';
import {getVisualizationConfigGQLFromStep} from 'analysis-tool/data/StepConfigUtils';
import {assert} from 'Asserts';
import {
  GetRandomSubsetDataFrame,
  GetRandomSubsetDataFrameVariables,
} from 'data-frames/graphql/__generated/GetRandomSubsetDataFrame';
import DataFrameWarningBanner from 'data-frames/graphql/DataFrameWarningBanner';
import {
  DEFAULT_DATA_FRAME_ROW_LIMIT,
  GET_RANDOM_SUBSET_DATA_FRAME_QUERY,
} from 'data-frames/graphql/GetDataFrame';
import {PipelineStepStatusCode, PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  PARAMETERS_TYPENAME_TO_VISUALIZATION_TYPE,
  VISUALIZATION_TYPE_TO_FLEX_GROW,
} from 'insights/analytics-visualizations/AnalyticsVisualizationConstants';
import {VisualizationType} from 'insights/analytics-visualizations/AnalyticsVisualizationTypes';
import {getTableLineageAncestorSteps} from 'pipelines/StepLineageUtils';
import getTestProps from 'test/lib/cypress/getTestProps';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import EditableTextField from 'ui-library/inputs/EditableTextField';
import LoadingOverlay from 'ui-library/LoadingOverlay';
import runConfirmDeleteModal from 'ui-library/modals/runConfirmDeleteModal';
import {omit} from 'util/omitKey';
import {withLoadingScreen} from 'util/Promises';

import getPersistedVisualizationConfig from '../charting/getPersistedVisualizationConfig';
import VisualizationBlock from '../charting/VisualizationBlock';
import StepLockedWarningBanner from '../components/StepLockedWarningBanner';
import {getSaveToResultsOverflowOption} from './MainViewHelpers';
import {AnalysisMainViewItemType, MainViewComponentProps} from './MainViewTypes';

/**
 * Component to show a view created by a `VISUALIZATION` analysis step
 *
 * @package
 */
export default function VisualizationView({
  clientStateId,
  analysis,
  onChangeSelectedItem,
  onOpenStepInitializationView,
  step: visualizationStep,
}: MainViewComponentProps<AnalysisMainViewItemType.VISUALIZATION>): JSX.Element | null {
  const {visualizationConfig, visualizationName} = getVisualizationConfigGQLFromStep(visualizationStep);
  const visualizationType =
    PARAMETERS_TYPENAME_TO_VISUALIZATION_TYPE[visualizationConfig.parameters.__typename];
  const viewSteps = getTableLineageAncestorSteps(analysis, visualizationStep);
  const inputStepData = findAndAssertStepData(visualizationStep, SINGLE_DATASET_INPUT_STEP_DATA_NAME, true);
  if (inputStepData.dataFrames.length === 0) {
    return <CenteredHorizontalThrobber />;
  }

  const inputDataFrameId = findAndAssertSingleStepDataFrameId(
    visualizationStep,
    SINGLE_DATASET_INPUT_STEP_DATA_NAME,
    true
  );

  async function handleSaveChartName(newName: string): Promise<void> {
    if (newName === visualizationName) {
      return;
    }
    await withLoadingScreen(
      updateAndRunAnalysisStep(analysis.id, {
        stepId: visualizationStep.id,
        stepConfig: {
          visualizationConfig: getPersistedVisualizationConfig(visualizationConfig),
          visualizationName: newName,
        },
      }),
      'Updating view...'
    );
  }

  const copyView = (): void => {
    withLoadingScreen(copyAnalysisViewSteps(analysis.id, viewSteps[0].id));
  };

  const deleteView = (): void => {
    runConfirmDeleteModal({typeName: 'view'}).then((confirmed) => {
      if (!confirmed) {
        return;
      }
      return withLoadingScreen(
        deleteAnalysisSteps(analysis.id, {
          stepIds: [viewSteps[0].id],
          behavior: DeletePipelineStepsBehavior.DELETE_ALL_CHILDREN,
        })
      );
    });
  };

  const promoteView = (): void => {
    withLoadingScreen(
      addOrReplaceAndRunAnalysisStep(analysis.id, {
        stepType: PipelineStepType.PROMOTE_DATASET,
        stepVersion: 0,
        stepConfig: {},
        stepInput: {
          inputDataFrameId: visualizationStep.outputStepDatas[0].dataFrames[0].id,
        },
        stepInputParents: {
          [SINGLE_DATASET_INPUT_STEP_DATA_NAME]: [
            {
              stepId: visualizationStep.id,
              stepDataName: SINGLE_DATASET_OUTPUT_STEP_DATA_NAME,
            },
          ],
        },
      })
    );
  };

  return (
    <BenchlingQuery<GetRandomSubsetDataFrame, GetRandomSubsetDataFrameVariables>
      query={GET_RANDOM_SUBSET_DATA_FRAME_QUERY}
      renderLoading={() => <CenteredHorizontalThrobber />}
      variables={{dataFrameId: inputDataFrameId, limit: DEFAULT_DATA_FRAME_ROW_LIMIT}}
      renderCachedDataWhenRefetching={false}
    >
      {({dataFrame}, result) => {
        assert(!isInaccessible(dataFrame), 'User does not have access to step input');

        return (
          <LoadingOverlay
            isLoading={
              (!viewSteps.some((step) => step.statusCode === PipelineStepStatusCode.FAILED) &&
                viewSteps.some((step) =>
                  [
                    PipelineStepStatusCode.BLOCKED,
                    PipelineStepStatusCode.WAITING,
                    PipelineStepStatusCode.RUNNING,
                  ].includes(step.statusCode)
                )) ||
              result.loading
            }
            isFaded={true}
          >
            <div
              className={css(
                visualizationStyles.wrapper,
                VISUALIZATION_TYPE_TO_FLEX_GROW[visualizationType] && visualizationStyles.fullHeight
              )}
              {...getTestProps({component: 'VisualizationView', element: 'root'})}
            >
              <div className={css(visualizationStyles.banner)}>
                <StepLockedWarningBanner analysis={analysis} step={visualizationStep} />
              </div>
              <VisualizationBlock
                analysis={analysis}
                banner={
                  <DataFrameWarningBanner
                    allColumnsJson={dataFrame.allColumnsJson}
                    dataFrame={dataFrame.randomSubsetDataFrameJson}
                    isRandomSubset={visualizationType !== VisualizationType.TABLE}
                    name={visualizationName}
                    totalRows={dataFrame.numRows}
                  />
                }
                clientStateId={clientStateId}
                dataFrame={dataFrame.randomSubsetDataFrameJson}
                dataFrameId={dataFrame.id}
                viewSteps={viewSteps}
                visualizationConfig={visualizationConfig}
                title={
                  analysis.permissions.writable ? (
                    <EditableTextField
                      value={visualizationName}
                      requestChange={handleSaveChartName}
                      dataTestAttrs={{component: 'EditableTextField', element: 'title'}}
                    />
                  ) : (
                    visualizationName
                  )
                }
                saveToResultsOverflowOption={
                  visualizationStep.outputStepDatas[0]?.dataFrames?.length > 0
                    ? getSaveToResultsOverflowOption(
                        analysis,
                        visualizationStep,
                        visualizationStep.outputStepDatas[0].dataFrames[0].id,
                        onChangeSelectedItem,
                        onOpenStepInitializationView
                      )
                    : null
                }
                onDeleteClicked={deleteView}
                onPromoteClicked={promoteView}
                onCopyClicked={copyView}
                onEditClicked={(): void => {
                  onChangeSelectedItem({
                    type: AnalysisMainViewItemType.EDIT_VISUALIZATION,
                    stepId: visualizationStep.id,
                  });
                }}
              />
            </div>
          </LoadingOverlay>
        );
      }}
    </BenchlingQuery>
  );
}

export const visualizationStyles = StyleSheet.create({
  banner: {
    marginBottom: 10,
  },
  wrapper: {
    // There is a box shadow on PresentationalUniversalBlockView so no need to have it here as well,
    // otherwise the shadows overlap making it look like there's one extra-dark shadow.
    ...omit(SHARED_MAIN_VIEW_COMPONENT_WITH_MARGIN_STYLES, 'boxShadow'),
  },
  fullHeight: {
    height: '100%',
  },
});
